/**
 * 付费 cohort adapter（ADM-MTR-S014）：GET /api/v1/metrics/first-payment-cohorts 的 snake_case DTO
 * → 页面消费的 camelCase 行，并派生各桶转化率（分母为 0 → null，页面显示 —）。
 *
 * 派生口径（dev-docs AD §17.3）：
 * - 桶转化率 = 桶首付人数 ÷ 激活数；累计付费率 = payers ÷ 激活数（= 各桶转化率之和）
 * - 观察未满 = matured.<桶> < 激活数（该组仍有激活用户没过完这个桶）
 */

import {
  FIRST_PAYMENT_COHORT_BUCKETS,
  type FirstPaymentCohortBucket,
  type FirstPaymentCohortData,
  type FirstPaymentCohortDimension,
  type FirstPaymentCohortMetrics,
} from '@novel-admin/shared';
import { ratio } from './metrics-paywall-conversion-adapter';

export type FirstPaymentCohortBucketView = {
  count: number;
  rate: number | null;
  /** 已过完该桶全部天数的激活数 */
  matured: number;
  /** 尚有激活用户未进入该桶观察期：桶人数是下限 */
  immature: boolean;
};

export type FirstPaymentCohortMetricsView = {
  activations: number;
  payers: number;
  payerRate: number | null;
  buckets: Record<FirstPaymentCohortBucket, FirstPaymentCohortBucketView>;
};

export type FirstPaymentCohortRowView = FirstPaymentCohortMetricsView & {
  key: string;
  dims: Partial<Record<FirstPaymentCohortDimension, string | null>>;
};

export type FirstPaymentCohortViewData = {
  window: { from: string; to: string };
  observedUntil: string;
  groupBy: FirstPaymentCohortDimension[];
  summary: FirstPaymentCohortMetricsView;
  rows: FirstPaymentCohortRowView[];
  truncated: boolean;
  options: FirstPaymentCohortData['options'];
  generatedAt: string;
};

function toView(m: FirstPaymentCohortMetrics): FirstPaymentCohortMetricsView {
  const buckets = Object.fromEntries(
    FIRST_PAYMENT_COHORT_BUCKETS.map(({ key }) => {
      const matured = m.matured[key];
      return [
        key,
        {
          count: m[key],
          rate: ratio(m[key], m.activations),
          matured,
          immature: matured < m.activations,
        },
      ];
    }),
  ) as Record<FirstPaymentCohortBucket, FirstPaymentCohortBucketView>;
  return {
    activations: m.activations,
    payers: m.payers,
    payerRate: ratio(m.payers, m.activations),
    buckets,
  };
}

export function adaptMetricsFirstPaymentCohorts(
  dto: FirstPaymentCohortData,
): FirstPaymentCohortViewData {
  return {
    window: { from: dto.window.from, to: dto.window.to },
    observedUntil: dto.observed_until,
    groupBy: dto.group_by,
    summary: toView(dto.summary),
    rows: dto.rows.map((row) => ({
      // 维度值是自由文本，整组 JSON 序列化才能保证 key 唯一且 null 不与字面值相撞
      key: JSON.stringify(dto.group_by.map((dimension) => row.dims[dimension] ?? null)),
      dims: row.dims,
      ...toView(row),
    })),
    truncated: dto.truncated,
    options: dto.options,
    generatedAt: dto.generated_at,
  };
}
